import React, { useRef, useEffect, useState } from 'react';
import './AppMenu.css';
import { json } from '../api/apiClient';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { queryClient } from '../queryClient';
import { useToast } from '../ui/Toast';
import { Sun, Moon, Monitor, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const THEME_OPTIONS = [
  { value: 'light',  label: 'Hell',    Icon: Sun     },
  { value: 'dark',   label: 'Dunkel',  Icon: Moon    },
  { value: 'system', label: 'System',  Icon: Monitor },
];

export default function AppMenu({ isOpen, onClose, onReloadCollection, onUploadCsv, isLoading }) {
  const menuRef = useRef(null);
  const [preferences, setPreferences] = useState({ cardLanguage: 'en', uiLanguage: 'de', highQualityImagesOnly: true });
  const [savingPref, setSavingPref] = useState(false);
  const navigate = useNavigate();
  const { logout, isAdmin } = useAuth();
  const { theme, setTheme } = useTheme();
  const toast = useToast();

  // Kartensprache und Bildqualitäts-Schwelle stecken in jeder Query, die Kartenbilder
  // liefert (Sammlung, Deck-Editor, Wantlist, …) — deshalb pauschal alles invalidieren
  // statt eine Key-Liste zu pflegen, die bei neuen Seiten stillschweigend unvollständig wird.
  const invalidateCardImageQueries = () => queryClient.invalidateQueries();

  // Load preferences when menu opens
  useEffect(() => {
    if (!isOpen) return;
    json('/api/me/preferences')
      .then((data) => {
        if (data?.preferences) setPreferences({
          cardLanguage: data.preferences.cardLanguage || 'en',
          uiLanguage: data.preferences.uiLanguage || 'de',
          highQualityImagesOnly: data.preferences.highQualityImagesOnly !== false,
        });
      })
      .catch((err) => {
        if (err && err.status === 401) {
          logout();
          navigate('/auth', { replace: true });
          return;
        }
        console.error('[AppMenu] Fehler beim Laden der Einstellungen:', err);
      });
  }, [isOpen, logout, navigate]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      // Capture-Phase: Knöpfe, die den Druck anhalten (Umdreh-Knopf von CardImage), schließen
      // das Menü sonst nicht.
      document.addEventListener('mousedown', handleClickOutside, { capture: true });
      return () => document.removeEventListener('mousedown', handleClickOutside, { capture: true });
    }
  }, [isOpen, onClose]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleChangeCardLanguage = async (e) => {
    const newLang = e.target.value;
    if (!newLang || newLang === preferences.cardLanguage) return;
    try {
      setSavingPref(true);
      const updated = await json('/api/me/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardLanguage: newLang }),
      });
      const pref = updated?.preferences || {};
      setPreferences((prev) => ({ ...prev, cardLanguage: pref.cardLanguage || newLang }));
      invalidateCardImageQueries();
    } catch (err) {
      if (err && err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('[AppMenu] Fehler beim Speichern der Kartensprache:', err);
      toast.push({ tone: 'error', message: 'Kartensprache konnte nicht gespeichert werden.' });
    } finally {
      setSavingPref(false);
    }
  };

  const handleChangeHighQualityImagesOnly = async (checked) => {
    const previous = preferences.highQualityImagesOnly;
    setPreferences((prev) => ({ ...prev, highQualityImagesOnly: checked }));
    try {
      setSavingPref(true);
      const updated = await json('/api/me/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ highQualityImagesOnly: checked }),
      });
      const pref = updated?.preferences || {};
      setPreferences((prev) => ({ ...prev, highQualityImagesOnly: pref.highQualityImagesOnly !== false }));
      invalidateCardImageQueries();
    } catch (err) {
      if (err && err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      // Optimistischen Haken zurücknehmen — die Checkbox darf keinen Zustand zeigen,
      // den der Server nie übernommen hat.
      setPreferences((prev) => ({ ...prev, highQualityImagesOnly: previous }));
      console.error('[AppMenu] Fehler beim Speichern von "Nur hochwertige Bilder":', err);
      toast.push({ tone: 'error', message: 'Einstellung konnte nicht gespeichert werden.' });
    } finally {
      setSavingPref(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div ref={menuRef} className="app-menu">
      {/* Kartenbilder-Sprache */}
      <div className="app-menu__section">
        <label htmlFor="cardLang" className="app-menu__section-label">
          Kartenbilder-Sprache
        </label>
        <select
          id="cardLang"
          value={preferences.cardLanguage}
          onChange={handleChangeCardLanguage}
          disabled={savingPref}
          className="app-menu__select"
        >
          <option value="en">Englisch</option>
          <option value="de">Deutsch</option>
        </select>

        {preferences.cardLanguage === 'de' && (
          <div className="app-menu__checkbox-row">
            <label className="app-menu__checkbox-label">
              <input
                type="checkbox"
                checked={preferences.highQualityImagesOnly}
                onChange={(e) => handleChangeHighQualityImagesOnly(e.target.checked)}
                disabled={savingPref}
              />
              Nur hochwertige Bilder
            </label>
            <p className="app-menu__hint">
              Deutsche Bilder haben häufiger niedrigere Qualität. Wenn eingeschaltet, werden bei Bildern mit geringerer Auflösung die englischen Versionen angezeigt.
            </p>
          </div>
        )}
      </div>

      {/* Darstellung / Theme */}
      <div className="app-menu__section">
        <span className="app-menu__section-label">Darstellung</span>
        <div className="app-menu__theme-buttons">
          {THEME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setTheme(opt.value)}
              className={`app-menu__theme-btn${theme === opt.value ? ' active' : ''}`}
              title={opt.label}
            >
              <opt.Icon size={14} aria-hidden />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Admin Dashboard Link */}
      {isAdmin && (
        <button
          onClick={() => { onClose(); navigate('/admin'); }}
          style={{
            display: 'block',
            width: '100%',
            padding: '12px',
            backgroundColor: 'transparent',
            color: 'var(--danger)',
            border: 'none',
            borderBottom: '1px solid var(--border)',
            cursor: 'pointer',
            textAlign: 'left',
            fontSize: '14px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <Settings size={14} aria-hidden /> Admin Dashboard
        </button>
      )}

      {/* Legal Links + Disclaimer */}
      <div className="app-menu__section" style={{ padding: '8px 0', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '8px' }}>
          <Link
            to="/impressum"
            onClick={onClose}
            style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', textDecoration: 'none' }}
          >
            Impressum
          </Link>
          <span style={{ margin: '0 8px', color: 'var(--text-muted)' }}>·</span>
          <Link
            to="/datenschutz"
            onClick={onClose}
            style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', textDecoration: 'none' }}
          >
            Datenschutz
          </Link>
        </div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 'var(--leading-normal)', margin: '0' }}>
          Unofficial Fan Content ({' '}
          <a href="https://company.wizards.com/en/legal/fancontentpolicy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)' }}>Fan Content Policy</a>
          ). ©Wizards of the Coast LLC. Card data by{' '}
          <a href="https://scryfall.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)' }}>Scryfall</a>.
        </p>
      </div>
    </div>
  );
}
